import { useEffect, useRef, useState } from 'react';

import { useSale } from '../../../../hooks/sale/useSale';

import useProductCatalog from '../../../../hooks/productpanel/useProductCatalog';

import POSCategoryScrollButton from '../categories/POSCategoryScrollButton';
import POSCategoryMoreButton from '../categories/POSCategoryMoreButton';
import POSCategoryClearButton from '../categories/POSCategoryClearButton';
import CategoryModal from '../../ProductBrowser/Category/CategoryModal';
import POSCategoryList from '../categories/POSCategoryList';

const POSProductCategories = () => {
  // =====================================================
  // 🔹 State
  // =====================================================

  const [showModal, setShowModal] = useState(false);

  // =====================================================
  // 🔹 Refs
  // =====================================================

  const scrollRef = useRef<HTMLDivElement>(null);

  // =====================================================
  // 🔹 Sale
  // =====================================================

  const { activeBranchId } = useSale();

  // =====================================================
  // 🔹 Sale State
  // =====================================================

  const branchId = activeBranchId;

  const isSaleReady = !!branchId;

  // =====================================================
  // 🔹 Product Catalog
  // =====================================================

  const {
    categories,
    fetchCategories,
    fetchFilteredProducts,
    selectedCategoryId,
    setSelectedCategory,
    pageSize,
  } = useProductCatalog(branchId);

  // =====================================================
  // 🚀 Init
  // =====================================================

  useEffect(() => {
    if (!branchId) {
      return;
    }

    fetchCategories();
  }, [branchId]);

  // =====================================================
  // 🔹 Handlers
  // =====================================================

  const handleCategoryClick = (categoryId: number) => {
    if (!branchId) {
      return;
    }

    setSelectedCategory(categoryId);

    fetchFilteredProducts(
      {
        categoryId,
        branchId,
      },
      0,
      pageSize
    );
  };

  const handleClearSelection = () => {
    if (!branchId) {
      return;
    }

    setSelectedCategory(null);

    fetchFilteredProducts(
      {
        branchId,
      },
      0,
      pageSize
    );
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) {
      return;
    }

    scrollRef.current.scrollBy({
      left: direction === 'left' ? -180 : 180,

      behavior: 'smooth',
    });
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <>
      <div
        className="
          flex
          items-center
          gap-2
        "
      >
        {/* ===================================================== */}
        {/* 🔹 Scroll Area */}
        {/* ===================================================== */}

        <div
          className="
            flex
            min-w-0
            flex-1
            items-center
            gap-2
          "
        >
          <POSCategoryScrollButton
            direction="left"
            disabled={!isSaleReady}
            onClick={() => handleScroll('left')}
          />

          <POSCategoryList
            scrollRef={scrollRef}
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            isSaleInitialized={isSaleReady}
            onCategoryClick={handleCategoryClick}
          />

          <POSCategoryScrollButton
            direction="right"
            disabled={!isSaleReady}
            onClick={() => handleScroll('right')}
          />
        </div>

        {/* ===================================================== */}
        {/* 🔹 Actions */}
        {/* ===================================================== */}

        <div
          className="
            flex
            shrink-0
            items-center
            gap-2
          "
        >
          <POSCategoryMoreButton
            disabled={!isSaleReady}
            onClick={() => setShowModal(true)}
          />

          {selectedCategoryId && (
            <POSCategoryClearButton onClick={handleClearSelection} />
          )}
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Modal */}
      {/* ===================================================== */}

      <CategoryModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

export default POSProductCategories;
